import scrollIntoView from 'scroll-into-view-if-needed';
import { animate, easeInOut } from 'popmotion';
import Styler from 'stylefire';

/**
 * Given an ID, scrolls to the element that has that ID. If the user prefers reduced motion, the
 * scroll is completed without animation. Otherwise, the scroll is smoothed with an easing function.
 * This is implemented with scroll-into-view-if-needed in order to provide a consistent experience
 * across browsers, including those that don't support element.scrollIntoView.
 *
 * @returns a Promise that resolves to true if the scroll was completed, or false if not
 */
export const scrollToElement = (id: string): Promise<boolean> =>
    new Promise(resolve => {
        const element = document.getElementById(id);

        if (element) {
            const prefersReducedMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)',
            ).matches;

            scrollIntoView(element, {
                behavior: actions => {
                    actions.forEach(action => {
                        const { top, el } = action;

                        if (prefersReducedMotion) {
                            // If the user prefers reduced motion, scroll without animation
                            el.scrollTop = top;
                            resolve(true);
                        } else {
                            // Otherwise, scroll with animation
                            const styler = Styler(el);

                            animate({
                                from: el.scrollTop,
                                to: top,
                                ease: easeInOut,
                                onUpdate: nextTop => {
                                    styler.set('scrollTop', nextTop);
                                },
                                onComplete: () => {
                                    resolve(true);
                                },
                            });
                        }
                    });
                },
            });
        }

        resolve(false);
    });
